import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { parentDashboardService } from '../services/parentDashboardService';
import { authService } from '../services/authService';
import type {
  ParentDashboardData,
  Kid,
  PendingKid,
  Activity,
  MessageThread,
  QuickMessage,
  DashboardStats
} from '../services/parentDashboardService';
import ChildManagementDialog from '../components/ChildManagementDialog';
import abacusService, { ParentAbacusDetailDTO, ParentAbacusSummaryDTO, TutorSessionOffer } from '../services/abacusService';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Badge,
  Chip,
  Stack,
  Paper,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Person,
  School,
  TrendingUp,
  Add,
  Book,
  Assignment,
  EmojiEvents,
  Message,
  Reply,
  AccessTime,
  Schedule,
  FilterList,
  VpnKey,
  HourglassEmpty,
} from '@mui/icons-material';


const ParentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [parentName, setParentName] = useState('');
  const [kids, setKids] = useState<Kid[]>([]);
  const [pendingKids, setPendingKids] = useState<PendingKid[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedKidFilter, setSelectedKidFilter] = useState<number | 'all'>('all');
  const [showChildManagementDialog, setShowChildManagementDialog] = useState(false);
  const [childDialogInitialTab, setChildDialogInitialTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageThreads, setMessageThreads] = useState<MessageThread[]>([]);
  const [quickMessages, setQuickMessages] = useState<QuickMessage[]>([]);
  const [stats, setStats] = useState<DashboardStats>({ activeLearners: 0, recentActivities: 0, unreadMessages: 0, weeklyAchievements: 0 });
  const [abacusSummaryByChild, setAbacusSummaryByChild] = useState<Record<number, ParentAbacusSummaryDTO>>({});
  const [abacusDetailChildId, setAbacusDetailChildId] = useState<number | null>(null);
  const [abacusDetail, setAbacusDetail] = useState<ParentAbacusDetailDTO | null>(null);
  const [abacusDetailLoading, setAbacusDetailLoading] = useState(false);
  const [tutorOffer, setTutorOffer] = useState<TutorSessionOffer | null>(null);
  const [tutorWhen, setTutorWhen] = useState('');
  const [tutorJoinUrl, setTutorJoinUrl] = useState<string | null>(null);
  const [tutorBookingError, setTutorBookingError] = useState<string | null>(null);

  // Load dashboard data from API
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const dashboardData: ParentDashboardData = await parentDashboardService.getParentDashboard();
      
      // Use API data or fallback to logged-in user's name
      const userData = localStorage.getItem('user');
      const fallbackName = userData ? JSON.parse(userData).firstName + ' ' + JSON.parse(userData).lastName : 'Parent';
      setParentName(dashboardData.parentName || fallbackName);
      setKids(dashboardData.children.map(child => ({
        id: child.id,
        name: child.name,
        age: child.age,
        grade: child.grade,
        avatar: child.avatar,
        overallProgress: child.overallProgress,
        completedCourses: child.completedCourses,
        activeCourses: child.activeCourses,
        lastActivity: child.lastActivity,
        currentCourse: child.currentCourse
      })));
      setPendingKids(dashboardData.pendingChildren ?? []);
      
      setActivities(dashboardData.recentActivities);
      
      setMessageThreads(dashboardData.messageThreads);
      setQuickMessages(dashboardData.quickMessages);
      setStats(dashboardData.stats);

      try {
        const summaries = await abacusService.getParentAbacusSummaries();
        const summaryMap = summaries.reduce<Record<number, ParentAbacusSummaryDTO>>((acc, summary) => {
          acc[summary.childId] = summary;
          return acc;
        }, {});
        setAbacusSummaryByChild(summaryMap);
      } catch {
        setAbacusSummaryByChild({});
      }
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Fallback to static data if API fails
      setParentName('Parent User');
      setKids([]);
      setActivities([]);
      setMessageThreads([]);
      setQuickMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Get user data from localStorage and load data
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setParentName(`${user.firstName} ${user.lastName}`);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    loadDashboardData();

    // Listen for child enrollment events to refresh data
    const handleChildEnrolled = () => {
      loadDashboardData();
    };
    window.addEventListener('childEnrolled', handleChildEnrolled);

    return () => {
      window.removeEventListener('childEnrolled', handleChildEnrolled);
    };
  }, []);

  const openAbacusDetail = async (childId: number) => {
    setAbacusDetailChildId(childId);
    setAbacusDetail(null);
    setTutorOffer(null);
    setTutorJoinUrl(null);
    setTutorBookingError(null);
    setAbacusDetailLoading(true);
    try {
      const detail = await abacusService.getParentAbacusDetail(childId);
      setAbacusDetail(detail);
      if (detail.enrollmentId) {
        setTutorOffer(await abacusService.getTutorSessionOffer(detail.enrollmentId));
      }
    } catch (error) {
      console.error('Error loading Abacus detail:', error);
      setAbacusDetail(null);
    } finally {
      setAbacusDetailLoading(false);
    }
  };

  const bookTutorSession = async () => {
    if (!abacusDetail?.enrollmentId || !tutorOffer?.bookable || !tutorWhen || !tutorOffer.suggestedTopicId) return;
    try {
      setTutorBookingError(null);
      const booked = await abacusService.bookTutorSession(abacusDetail.enrollmentId, {
        scheduledAt: tutorWhen,
        plannedTopicIds: [tutorOffer.suggestedTopicId],
      });
      setTutorJoinUrl(booked.joinUrl ?? null);
    } catch {
      setTutorBookingError('Could not book a tutor session.');
    }
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'course_completed':
        return <Book color="success" />;
      case 'quiz_passed':
        return <Assignment color="primary" />;
      case 'badge_earned':
        return <EmojiEvents color="warning" />;
      case 'lesson_started':
        return <School color="info" />;
      case 'assignment_submitted':
        return <Assignment color="secondary" />;
      default:
        return <School />;
    }
  };


  const filteredActivities = selectedKidFilter === 'all' 
    ? activities 
    : activities.filter(activity => activity.childId === selectedKidFilter);

  const handleManageChildren = () => {
    setChildDialogInitialTab(0);
    setShowChildManagementDialog(true);
  };

  const handleRegisterNewChild = () => {
    setChildDialogInitialTab(0); // Register New Child tab
    setShowChildManagementDialog(true);
  };

  const handleAddRegisteredChild = () => {
    setChildDialogInitialTab(1); // Add Registered Child tab
    setShowChildManagementDialog(true);
  };

  const handleCompletePendingLink = () => {
    setChildDialogInitialTab(2); // Pending Links tab
    setShowChildManagementDialog(true);
  };

  const handleCloseChildManagementDialog = () => {
    setShowChildManagementDialog(false);
  };

  const handleChildRegistered = (childData: any) => {
    // Refresh the dashboard data to include the new child
    loadDashboardData();
  };

  const handleChildLinked = (childId: number) => {
    // Refresh the dashboard data to include the linked child
    loadDashboardData();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: 'text.primary',
              mb: 1
            }}
          >
            Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {parentName ? parentName.split(' ')[0] : 'Parent'}! 👋
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Here's what's happening with your children's learning journey
          </Typography>
        </Box>

        {/* Quick Stats Overview */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3, 
          mb: 4 
        }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats.activeLearners}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Learners
                  </Typography>
                </Box>
                <School sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats.recentActivities}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Recent Activities
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats.unreadMessages}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Unread Messages
                  </Typography>
                </Box>
                <Message sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats.weeklyAchievements}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    This Week's Achievements
                  </Typography>
                </Box>
                <EmojiEvents sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Children Overview */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Your Children
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleManageChildren}
              sx={{ 
                borderRadius: 3,
                px: 3,
                py: 1,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
              }}
            >
              Manage Children
            </Button>
          </Box>

          {kids.length === 0 && pendingKids.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.50', border: '2px dashed', borderColor: 'primary.main' }}>
              <Box sx={{ mb: 3 }}>
                <Person sx={{ fontSize: 64, color: 'primary.main' }} />
              </Box>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                No child is registered with parent account
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                Start tracking your children's learning progress by adding them to your account. 
                You'll be able to monitor their courses, achievements, and help them succeed!
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleRegisterNewChild}
                  sx={{ borderRadius: 2 }}
                >
                  Register a new child
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Person />}
                  onClick={handleAddRegisteredChild}
                  sx={{ borderRadius: 2 }}
                >
                  Add a registered child
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<VpnKey />}
                  onClick={handleCompletePendingLink}
                  sx={{ borderRadius: 2 }}
                >
                  Enter verification code
                </Button>
              </Box>
            </Card>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3
            }}>
              {pendingKids.map((pending) => (
                <Box key={`pending-${pending.id}`}>
                  <Card
                    sx={{
                      position: 'relative',
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '2px dashed',
                      borderColor: 'warning.main',
                      bgcolor: 'grey.100',
                      opacity: 0.9,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 120,
                        background: 'linear-gradient(135deg, #9e9e9e 0%, #616161 100%)',
                      }}
                    />
                    <CardContent sx={{ position: 'relative', pt: 4, pb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Avatar
                          sx={{
                            width: 100,
                            height: 100,
                            fontSize: 36,
                            bgcolor: 'white',
                            color: 'grey.600',
                            border: '4px solid white',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            fontWeight: 700,
                          }}
                        >
                          {pending.avatar || pending.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </Box>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: 'text.primary' }}>
                          {pending.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'warning.main' }}>
                          <HourglassEmpty fontSize="small" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Awaiting verification
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          This child has not yet verified their email. Ask them to check their inbox.
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        color="warning"
                        fullWidth
                        onClick={handleCompletePendingLink}
                        sx={{ borderRadius: 3, py: 1.5, fontWeight: 600 }}
                      >
                        Complete Verification
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              ))}
              {kids.map((kid) => (
                <Box key={kid.id}>
                  <Card 
                    sx={{ 
                      position: 'relative',
                      borderRadius: 4,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': { 
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                      }
                    }}
                    onClick={() => {
                      // Only navigate if child has courses, otherwise let button handle it
                      if (kid.activeCourses > 0 || (kid.overallProgress !== null && kid.overallProgress !== undefined)) {
                        navigate(`/parent-child-dashboard/${kid.id}`);
                      }
                    }}
                  >
                    {/* Background Pattern */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 120,
                        background: `linear-gradient(135deg, ${
                          kid.id === 1 ? '#667eea, #764ba2' : 
                          kid.id === 2 ? '#f093fb, #f5576c' : 
                          '#4facfe, #00f2fe'
                        })`,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                        }
                      }}
                    />
                    
                    <CardContent sx={{ position: 'relative', pt: 4, pb: 3 }}>
                      {/* Avatar */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Avatar
                          sx={{ 
                            width: 100, 
                            height: 100, 
                            fontSize: 36,
                            bgcolor: 'white',
                            color: 'primary.main',
                            border: '4px solid white',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            fontWeight: 700
                          }}
                        >
                          {kid.avatar}
                        </Avatar>
                      </Box>
                      
                      {/* Child Info */}
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: 'text.primary' }}>
                          {kid.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: kid.schoolName ? 0.5 : 2, fontWeight: 500 }}>
                          {kid.age != null ? `${kid.age} years old` : 'Age unknown'}{kid.grade ? ` • ${kid.grade}` : ''}
                        </Typography>
                        {kid.schoolName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {kid.schoolName}
                          </Typography>
                        )}
                        {abacusSummaryByChild[kid.id] && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="secondary.main"
                              data-testid={`abacus-parent-line-${kid.id}`}
                              sx={{
                                fontWeight: 600,
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {abacusCardLine(abacusSummaryByChild[kid.id])}
                            </Typography>
                            {abacusSummaryByChild[kid.id].levelBadges &&
                              abacusSummaryByChild[kid.id].levelBadges!.length > 0 && (
                              <Typography
                                variant="body2"
                                data-testid={`abacus-parent-badges-${kid.id}`}
                                sx={{ fontWeight: 600 }}
                              >
                                {abacusSummaryByChild[kid.id].levelBadges!.join(', ')}
                              </Typography>
                            )}
                            {abacusSummaryByChild[kid.id].tutorAided && (
                              <Chip
                                label="Tutor-aided"
                                size="small"
                                data-testid={`abacus-tutor-aided-${kid.id}`}
                                sx={{ mt: 0.5 }}
                              />
                            )}
                            <Button
                              size="small"
                              data-testid={`abacus-parent-detail-open-${kid.id}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                void openAbacusDetail(kid.id);
                              }}
                            >
                              Abacus details
                            </Button>
                          </Box>
                        )}
                      </Box>
                      
                      {/* Check if child has courses */}
                      {kid.activeCourses > 0 || (kid.overallProgress !== null && kid.overallProgress !== undefined) ? (
                        <>
                          {/* Progress Indicators */}
                          {kid.overallProgress !== null && kid.overallProgress !== undefined && (
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Overall Progress
                                </Typography>
                                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
                                  {Math.round(kid.overallProgress)}%
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                height: 8, 
                                borderRadius: 4, 
                                bgcolor: 'grey.200',
                                overflow: 'hidden'
                              }}>
                                <Box sx={{ 
                                  height: '100%', 
                                  width: `${kid.overallProgress}%`,
                                  background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                                  borderRadius: 4
                                }} />
                              </Box>
                            </Box>
                          )}
                          
                          {/* Quick Stats */}
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 2, 
                            mb: 3 
                          }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                                {kid.activeCourses}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Active Courses
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                                {kid.completedCourses}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Completed
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" color="info.main" sx={{ fontWeight: 700 }}>
                                {kid.lastActivity || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Last Activity
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Action Button */}
                          <Button 
                            variant="contained" 
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/parent-child-dashboard/${kid.id}`);
                            }}
                            sx={{ 
                              borderRadius: 3,
                              py: 1.5,
                              fontWeight: 600,
                              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                              boxShadow: 'none',
                              '&:hover': {
                                boxShadow: '0 4px 20px rgba(33, 150, 243, 0.4)',
                                background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                              }
                            }}
                          >
                            View Dashboard
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* No Courses Message */}
                          <Box sx={{ 
                            mb: 3, 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 2,
                            textAlign: 'center'
                          }}>
                            <School sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                              No courses enrolled
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Register your child for a course to start tracking their progress
                            </Typography>
                          </Box>
                          
                          {/* Register Course Button */}
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/parent-register-child/${kid.id}`);
                            }}
                            sx={{
                              borderRadius: 3,
                              py: 1.5,
                              fontWeight: 600,
                              background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                              boxShadow: 'none',
                              '&:hover': {
                                boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)',
                                background: 'linear-gradient(45deg, #388E3C 30%, #66BB6A 90%)',
                              }
                            }}
                          >
                            Register a Course
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Messages & Communication Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                Messages & Communication 💬
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stay connected with your children's teachers
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Message />}
                onClick={() => navigate('/parent-messages')}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  background: 'linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)',
                  boxShadow: '0 3px 5px 2px rgba(255, 107, 107, .3)',
                }}
              >
                View All Messages
              </Button>
              <Button
                variant="outlined"
                startIcon={<Reply />}
                onClick={() => navigate('/parent/messages/compose')}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                New Message
              </Button>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3 
          }}>
            {/* Recent Messages */}
            <Box>
              <Card sx={{ 
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.04)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Recent Conversations
                    </Typography>
                    <Chip 
                      label={`${messageThreads.reduce((sum, thread) => sum + thread.unreadCount, 0)} unread`} 
                      color="primary" 
                      size="small" 
                    />
                  </Box>
                  
                  {messageThreads.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Message sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No messages yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Start a conversation with your child's teachers
                      </Typography>
                      <Button 
                        variant="contained" 
                        startIcon={<Reply />}
                        onClick={() => navigate('/parent/messages/compose')}
                      >
                        Send First Message
                      </Button>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {messageThreads.slice(0, 3).map((thread, index) => (
                        <React.Fragment key={thread.id}>
                          <ListItem 
                            sx={{ 
                              px: 0, 
                              py: 1.5,
                              cursor: 'pointer',
                              borderRadius: 1,
                              '&:hover': { bgcolor: 'grey.50' }
                            }}
                            onClick={() => navigate(`/parent/messages/thread/${thread.id}`)}
                          >
                            <ListItemAvatar>
                              <Badge badgeContent={thread.unreadCount} color="error">
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                  {thread.teacherName.charAt(0)}
                                </Avatar>
                              </Badge>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography 
                                    variant="subtitle2" 
                                    sx={{ fontWeight: thread.unreadCount > 0 ? 600 : 400 }}
                                  >
                                    {thread.teacherName}
                                  </Typography>
                                  {thread.priority === 'high' && (
                                    <Chip label="High Priority" color="warning" size="small" />
                                  )}
                                  {thread.priority === 'urgent' && (
                                    <Chip label="Urgent" color="error" size="small" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box component="span">
                                  <Typography variant="body2" sx={{ fontWeight: 500, display: 'block' }} component="span">
                                    {thread.subject}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary" 
                                    sx={{ 
                                      display: '-webkit-box',
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                    component="span"
                                  >
                                    {thread.lastMessage}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }} component="span">
                                    <AccessTime sx={{ fontSize: 12 }} />
                                    {thread.lastActivity}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < messageThreads.slice(0, 3).length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                  
                  {messageThreads.length > 3 && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button 
                        variant="text" 
                        onClick={() => navigate('/parent-messages')}
                      >
                        View {messageThreads.length - 3} more conversations
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Quick Actions */}
            <Box>
              <Card sx={{ 
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.04)'
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Quick Actions
                  </Typography>
                  
                  <Stack spacing={2}>
                    {quickMessages.map((quickMsg) => (
                      <Paper 
                        key={`${quickMsg.teacherId}-${quickMsg.subject}`}
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { 
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50' 
                          }
                        }}
                        onClick={() => navigate(`/parent/messages/compose?teacherId=${quickMsg.teacherId}&subject=${encodeURIComponent(quickMsg.subject)}&childId=1`)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'secondary.main' }}>
                            {quickMsg.teacherName.charAt(0)}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {quickMsg.teacherName}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {quickMsg.subject}
                        </Typography>
                        <Typography variant="caption" color="primary.main">
                          Click to send message →
                        </Typography>
                      </Paper>
                    ))}
                    
                    <Button
                      variant="outlined"
                      startIcon={<Schedule />}
                      fullWidth
                      onClick={() => navigate('/parent-messages')}
                      sx={{ mt: 2 }}
                    >
                      Schedule Meeting
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* Recent Activity Feed */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                Recent Activity 🎯
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest achievements and progress updates
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Child</InputLabel>
                <Select
                  value={selectedKidFilter}
                  label="Filter by Child"
                  onChange={(e) => setSelectedKidFilter(e.target.value as number | 'all')}
                >
                  <MenuItem value="all">All Children</MenuItem>
                  {kids.map((kid) => (
                    <MenuItem key={kid.id} value={kid.id}>
                      {kid.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                size="small"
              >
                More Filters
              </Button>
            </Box>
          </Box>

          {filteredActivities.length === 0 ? (
            <Card sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.04)'
            }}>
              <Box sx={{ mb: 3 }}>
                <TrendingUp sx={{ fontSize: 64, color: 'text.disabled' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No activities to show
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedKidFilter === 'all' 
                  ? 'Your children haven\'t started any activities yet.' 
                  : 'This child hasn\'t started any activities yet.'}
              </Typography>
            </Card>
          ) : (
            <Card sx={{ 
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.04)',
              overflow: 'hidden'
            }}>
            <List>
              {filteredActivities.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'background.paper' }}>
                        {getActivityIcon(activity.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {activity.title}
                          </Typography>
                          {activity.score && (
                            <Chip 
                              label={`${activity.score}%`} 
                              size="small" 
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box component="span">
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'block' }} component="span">
                            {activity.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} component="span">
                            <Typography variant="caption" color="text.secondary" component="span">
                              {activity.childName} • {activity.courseName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="span">
                              {new Date(activity.timestamp).toLocaleDateString()} at {new Date(activity.timestamp).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < filteredActivities.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            </Card>
          )}
        </Box>

        {/* Quick Navigation */}
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              Quick Navigation 🚀
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Jump to the most important sections
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3 
          }}>
            <Box>
              <Card 
                sx={{ 
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer', 
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => navigate('/parent-courses')}
              >
                <Box sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  p: 3,
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <School sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Courses
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Browse available courses
                  </Typography>
                </Box>
              </Card>
            </Box>
            
            <Box>
              <Card 
                sx={{ 
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer', 
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => navigate('/parent-progress-reports')}
              >
                <Box sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  p: 3,
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <TrendingUp sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Progress Reports
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Detailed progress analysis
                  </Typography>
                </Box>
              </Card>
            </Box>
            
            <Box>
              <Card 
                sx={{ 
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer', 
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => navigate('/parent-achievements')}
              >
                <Box sx={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  p: 3,
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <EmojiEvents sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Achievements
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Badges & accomplishments
                  </Typography>
                </Box>
              </Card>
            </Box>
            
            <Box>
              <Card 
                sx={{ 
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer', 
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { 
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={handleManageChildren}
              >
                <Box sx={{
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  p: 3,
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <Add sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Manage Children
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Register another child
                  </Typography>
                </Box>
              </Card>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Child Management Dialog */}
      <ChildManagementDialog
        open={showChildManagementDialog}
        onClose={handleCloseChildManagementDialog}
        onChildRegistered={handleChildRegistered}
        onChildLinked={handleChildLinked}
        title="Manage Children"
        parentId={authService.getCurrentUser()?.id}
        initialTab={childDialogInitialTab}
      />

      <Dialog
        open={abacusDetailChildId != null}
        onClose={() => setAbacusDetailChildId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Abacus Maths</DialogTitle>
        <DialogContent data-testid="abacus-parent-detail">
          {abacusDetailLoading && <CircularProgress size={28} />}
          {!abacusDetailLoading && abacusDetail && (
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {abacusCardLine({
                  childId: abacusDetail.childId,
                  track: abacusDetail.track,
                  levelCode: abacusDetail.levelCode,
                  topicsCompleted: 0,
                  latestTestWeek: null,
                })}
              </Typography>
              {abacusDetail.tutorAided && <Chip label="Tutor-aided" size="small" sx={{ mb: 1 }} />}
              <Typography variant="body2" data-testid="abacus-parent-practice-count" sx={{ mb: 2 }}>
                {`Practice attempts this week: ${abacusDetail.practiceCountWeek}`}
              </Typography>
              {abacusDetail.testWeeks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No Test Week attempts yet
                </Typography>
              ) : (
                <List data-testid="abacus-parent-test-weeks" dense>
                  {abacusDetail.testWeeks.map((attempt, index) => (
                    <ListItem key={`${attempt.completedAt ?? 'attempt'}-${index}`} disablePadding>
                      <ListItemText
                        primary={`${attempt.title}: ${attempt.passed ? 'Passed' : 'Failed'} (${attempt.scorePercent}%)`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Box sx={{ mt: 2 }} data-testid="parent-tutor-session">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Tutor session
                </Typography>
                {tutorOffer?.upsell && (
                  <Alert severity="info" data-testid="tutor-session-upsell">
                    Tutor sessions are part of tutor-aided support. Lessons, practice, and Test Weeks stay available.
                  </Alert>
                )}
                {tutorOffer?.bookable && (
                  <Stack spacing={1}>
                    <TextField
                      label="Session time"
                      type="datetime-local"
                      value={tutorWhen}
                      onChange={(event) => setTutorWhen(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={() => void bookTutorSession()} disabled={!tutorWhen}>
                      Book tutor session
                    </Button>
                  </Stack>
                )}
                {tutorBookingError && <Alert severity="warning" sx={{ mt: 1 }}>{tutorBookingError}</Alert>}
                {tutorJoinUrl && (
                  <Typography variant="body2" sx={{ mt: 1 }} data-testid="tutor-session-join">
                    {tutorJoinUrl}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {!abacusDetailLoading && abacusDetailChildId != null && !abacusDetail && (
            <Typography variant="body2" color="text.secondary">
              Abacus details are not available for this child.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbacusDetailChildId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function abacusCardLine(summary: ParentAbacusSummaryDTO): string {
  const track = summary.track === 'SENIOR' ? 'Senior' : summary.track === 'JUNIOR' ? 'Junior' : summary.track;
  const level = summary.levelCode ? `Level ${summary.levelCode}` : '';
  let line = `Abacus Maths — ${track} ${level}`.replace(/\s+/g, ' ').trim();
  if (summary.latestTestWeek) {
    const outcome = summary.latestTestWeek.passed ? 'Passed' : 'Failed';
    line += `, ${summary.latestTestWeek.title}: ${outcome} (${summary.latestTestWeek.scorePercent}%)`;
  }
  return line;
}

export default ParentDashboard;
